/**
 * The development tools.
 *
 * Drawn only for accounts the server says hold a rank, and only ever a way to ask:
 * every button sends a named command and the server decides whether it happens.
 * Hiding a button is presentation, not security, and nothing here assumes
 * otherwise.
 *
 * Editing the world itself is the editor's job rather than this window's: this is
 * the panel for acting on people and on what they hold.
 */

import { Role, atLeast, SKILL_NAMES, type RoleValue } from '@inverse/common';
import type { AdminFactsPacket, ItemDefinitionWire } from '@inverse/protocol';
import { useEffect, useRef, useState } from 'react';
import { ColourChoice } from './Colours';
import { colourText, colourValue } from './colour-values';
import { useDraggableWindow } from './draggable';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';
import { CloseButton } from './CloseButton';
import { Explain } from './Explain';

export type AdminCommand = (
  command: string,
  numbers?: Record<string, number>,
  text?: Record<string, string>,
) => void;

type Section = 'players' | 'world' | 'server' | 'look' | 'peace';

/**
 * Running the server from inside it.
 *
 * The same facts the terminal panel shows and the same three levers: warn
 * everybody and take the world down, open the door again, and swap the content
 * without anybody noticing. Nothing here reaches past the server: bringing the
 * world back up is the service manager's job, which is why the restart button
 * says what it does rather than pretending to be a switch.
 */
/**
 * Holding the day still while somebody works.
 *
 * A world with an hour long day is a world where anything written for night is
 * mostly untestable, so the day can be held at a stretch and let go again. It
 * is written down with everything else the world remembers, so it survives a
 * restart the same way.
 */
function TimeSection({
  time,
  onCommand,
}: {
  time: { phase: string; held: boolean } | null;
  onCommand: AdminCommand;
}): JSX.Element {
  const phases = ['dawn', 'day', 'dusk', 'night'] as const;

  return (
    <>
      <h4>
        Time of day
        <Explain>
          The world keeps an hour long day off the wall clock, so it comes back after a restart at
          the time it went down. Hold it at a stretch to build for that stretch.
        </Explain>
      </h4>

      <p className="hint">
        {time ? `${time.phase}${time.held ? ', held there' : ', running on its own'}` : 'Asking.'}
      </p>

      <div className="editor-brushes">
        {phases.map((phase) => (
          <button
            key={phase}
            type="button"
            className={time?.held && time.phase === phase ? 'editor-tab on' : 'editor-tab'}
            onClick={() => onCommand('setTime', {}, { time: phase })}
          >
            {phase}
          </button>
        ))}
        <button
          type="button"
          className={time?.held ? 'editor-tab' : 'editor-tab on'}
          onClick={() => onCommand('setTime', {}, { time: '' })}
        >
          Let it run
        </button>
      </div>
    </>
  );
}

/**
 * What the world remembers about itself.
 *
 * Not a player's flags: these belong to nobody, and are how something that has
 * happened stays happened. Shown and changed here because trying a gate that
 * opens once should not mean opening it once.
 */
function WorldMemorySection({
  facts,
  onCommand,
}: {
  facts: AdminFactsPacket | null;
  onCommand: AdminCommand;
}): JSX.Element {
  const [name, setName] = useState('');
  const [worldItem, setWorldItem] = useState('');
  const [worldPrice, setWorldPrice] = useState('');
  const [value, setValue] = useState('yes');

  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    onCommand('worldFlags');
  }, [onCommand]);

  const held = facts?.kind === 'worldFlags' ? facts : null;

  let flags: { name: string; value: string }[] = [];
  try {
    const parsed: unknown = JSON.parse(held?.text.json ?? '[]');
    if (Array.isArray(parsed)) flags = parsed as { name: string; value: string }[];
  } catch {
    flags = [];
  }

  const priceOf = (flag: string): string => flags.find((one) => one.name === flag)?.value ?? '';

  return (
    <>
      <h4>
        What it costs to speak to everybody
        <Explain>
          A line every person in the world has to read is worth more than a line the people standing
          nearby have to read. Name an item and how many of it; leave either empty and it is free.
        </Explain>
      </h4>

      <div className="editor-brushes">
        <input
          type="number"
          min={0}
          value={worldItem}
          placeholder="item id"
          onChange={(event) => setWorldItem(event.target.value.slice(0, 8))}
        />
        <input
          type="number"
          min={0}
          value={worldPrice}
          placeholder="how many"
          onChange={(event) => setWorldPrice(event.target.value.slice(0, 8))}
        />
        <button
          type="button"
          onClick={() => {
            onCommand('setWorldFlag', {}, { flag: 'chat.world.item', value: worldItem.trim() });
            onCommand('setWorldFlag', {}, { flag: 'chat.world.price', value: worldPrice.trim() });
          }}
        >
          Set
        </button>
      </div>

      <p className="hint">
        {priceOf('chat.world.item') && priceOf('chat.world.price')
          ? `Now ${priceOf('chat.world.price')} of item ${priceOf('chat.world.item')} a line.`
          : 'Free at the moment.'}
      </p>

      <h4>
        What the world remembers
        <Explain>
          Flags that belong to the world rather than to a player. A gate somebody opened is open for
          everybody, and the next person along finds it open.
        </Explain>
      </h4>

      {flags.map((flag) => (
        <div className="editor-drop" key={flag.name}>
          <span className="editor-drop-name">{flag.name}</span>
          <span className="editor-drop-count">{flag.value}</span>
          <button
            type="button"
            className="editor-drop-remove"
            title="Forget it"
            onClick={() => onCommand('setWorldFlag', {}, { flag: flag.name, value: '' })}
          >
            ✕
          </button>
        </div>
      ))}

      {flags.length === 0 ? <p className="hint">It remembers nothing yet.</p> : null}

      <div className="editor-brushes">
        <input
          type="text"
          value={name}
          placeholder="flag"
          onChange={(event) => setName(event.target.value.slice(0, 48))}
        />
        <input
          type="text"
          value={value}
          placeholder="yes"
          onChange={(event) => setValue(event.target.value.slice(0, 48))}
        />
        <button
          type="button"
          disabled={name.trim().length === 0}
          onClick={() => {
            onCommand('setWorldFlag', {}, { flag: name.trim(), value });
            setName('');
          }}
        >
          Remember it
        </button>
      </div>

      <button type="button" className="wide" onClick={() => onCommand('worldFlags')}>
        Ask again
      </button>
    </>
  );
}

function ServerSection({
  facts,
  onCommand,
}: {
  facts: AdminFactsPacket | null;
  onCommand: AdminCommand;
}): JSX.Element {
  const [countdown, setCountdown] = useState(60);
  const held = facts?.kind === 'server' ? facts : null;

  // Asked for once, when the section is opened. The answer arrives as a fact like
  // any other, and asking again is a button rather than a render: a question that
  // asks itself every time the answer arrives never stops asking.
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    onCommand('serverState');
  }, [onCommand]);

  const number = (name: string): number => held?.numbers[name] ?? 0;
  const text = (name: string): string => held?.text[name] ?? '';

  const spell = (seconds: number): string => {
    if (seconds <= 0) return 'not running';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m ${seconds % 60}s`;
  };

  return (
    <>
      {held ? (
        <div className="server-facts">
          <div>
            <span>Shard</span>
            <strong>{text('shard')}</strong>
          </div>
          <div>
            <span>World</span>
            <strong>{text('region')}</strong>
          </div>
          <div>
            <span>Protocol</span>
            <strong>{number('protocol')}</strong>
          </div>
          <div>
            <span>Node</span>
            <strong>{text('node')}</strong>
          </div>
          <div>
            <span>Up</span>
            <strong>{spell(number('uptime'))}</strong>
          </div>
          <div>
            <span>Playing</span>
            <strong>{number('players')}</strong>
          </div>
          <div>
            <span>Alive</span>
            <strong>{number('npcs')}</strong>
          </div>
          <div>
            <span>Memory</span>
            <strong>{number('rssMb')} MB</strong>
          </div>
          <div>
            <span>Tick</span>
            <strong>{number('tickMs')} ms</strong>
          </div>
          <div>
            <span>Content</span>
            <strong>
              {text('items')} items, {text('characters')} characters, {text('pieces')} pieces
            </strong>
          </div>
          <div>
            <span>Logins</span>
            <strong>{number('logins') === 1 ? 'open' : 'closed'}</strong>
          </div>
          {number('restartIn') > 0 ? (
            <div>
              <span>Going down in</span>
              <strong>{number('restartIn')}s</strong>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="hint">Asking the server how it is.</p>
      )}

      {number('unsaved') === 1 ? (
        <p className="hint warning">
          Unsaved work in the world.
          <Explain>
            Save it before taking the server down, or it goes when the process does.
          </Explain>
        </p>
      ) : null}

      <button type="button" className="wide" onClick={() => onCommand('serverState')}>
        Ask again
      </button>

      <h4>Bringing it down</h4>
      <label className="editor-field">
        <span>Warning, in seconds</span>
        <input
          type="number"
          min={0}
          max={3600}
          value={countdown}
          onChange={(event) => setCountdown(Number(event.target.value))}
        />
      </label>

      <button
        type="button"
        className="wide"
        onClick={() => {
          onCommand('announceRestart', { seconds: countdown });
          onCommand('serverState');
        }}
      >
        Warn everybody and close the door
      </button>

      <button type="button" className="wide" onClick={() => onCommand('openLogins')}>
        Call it off and let people back in
      </button>

      <button
        type="button"
        className="wide danger"
        onClick={() => {
          onCommand('stopServer');
        }}
      >
        Take the world down now
      </button>

      <p className="hint">
        Everybody is flushed to the database first.
        <Explain>
          The world comes back on its own wherever the service is set to start at boot, which is a
          minute at most.
        </Explain>
      </p>
    </>
  );
}

/**
 * Keeping the peace: what has been reported, and what the chat filter catches.
 *
 * The reports come with what the person reported last said, as the server
 * heard it, so a moderator can judge without having been there.
 */
function ModerationSection({
  role,
  facts,
  onCommand,
}: {
  role: RoleValue;
  facts: AdminFactsPacket | null;
  onCommand: AdminCommand;
}): JSX.Element {
  const [reports, setReports] = useState<
    {
      at: string;
      by: string;
      about: string;
      reason: string;
      note: string;
      chat: { at: string; scope: string; text: string }[];
    }[]
  >([]);
  const [words, setWords] = useState('');
  const [seen, setSeen] = useState<AdminFactsPacket | null>(null);

  // What the server sent back, kept here: the tools only hold the latest.
  if (facts !== seen) {
    setSeen(facts);
    if (facts?.kind === 'reports') {
      try {
        setReports(JSON.parse(facts.text.json ?? '[]') as typeof reports);
      } catch {
        setReports([]);
      }
    }
    if (facts?.kind === 'chatFilter') setWords(facts.text.words ?? '');
  }

  useEffect(() => {
    onCommand('listReports', {}, {});
    onCommand('chatFilter', {}, {});
  }, [onCommand]);

  return (
    <>
      <h3>
        Reports <span className="panel-count">{reports.length}</span>
      </h3>
      <button type="button" className="wide" onClick={() => onCommand('listReports', {}, {})}>
        Look again
      </button>
      {reports.length === 0 ? (
        <p className="hint">Nobody has reported anybody since the world started.</p>
      ) : null}
      {reports.map((one, at) => (
        <div className="report-entry" key={at}>
          <strong>
            {one.about}: {one.reason}
          </strong>
          <span className="hint">
            from {one.by}, {new Date(one.at).toLocaleString()}
          </span>
          {one.note ? <p>{one.note}</p> : null}
          {one.chat.length > 0 ? (
            <ul className="report-chat">
              {one.chat.map((line, index) => (
                <li key={index}>
                  <span className="hint">{line.scope}</span> {line.text}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}

      <h3>Chat filter</h3>
      <p className="hint">
        Words starred out wherever anybody says them, one to a line or separated by commas.
        <Explain>
          Whole words only, so a longer word that happens to contain one is left alone. Numbers
          standing in for letters and letters stretched out are caught.
        </Explain>
      </p>
      <textarea
        className="report-note"
        value={words}
        readOnly={!atLeast(role, Role.SuperAdmin)}
        onChange={(event) => setWords(event.target.value)}
      />
      {atLeast(role, Role.SuperAdmin) ? (
        <button
          type="button"
          className="wide"
          onClick={() => onCommand('setChatFilter', {}, { words })}
        >
          Keep this list
        </button>
      ) : null}
    </>
  );
}

export function AdminWindow({
  role,
  online,
  items,
  editing,
  onEditing,
  unsaved,
  time,
  myLook,
  facts,
  openOn,
  onCommand,
  onClose,
}: {
  role: RoleValue;
  online: readonly string[];
  items: readonly ItemDefinitionWire[];
  /** Whether the world editor is open, which this window is the way in to. */
  editing: boolean;
  onEditing: (open: boolean) => void;
  /** Whether the world has changes nobody has written out. */
  unsaved: boolean;
  /** What time the world thinks it is, and whether somebody is holding it there. */
  time: { phase: string; held: boolean } | null;
  /** What the character being played looks like now. */
  myLook: { skin: number; eyes: number };
  /** Whatever the tools last asked about, which is where the server's own state arrives. */
  facts: AdminFactsPacket | null;
  /** Which section to open on, for the ways in that name one. */
  openOn?: Section;
  onCommand: AdminCommand;
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown, nudge } = useDraggableWindow('admin');
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips('admin', nudge, altHeld, {
    width: 360,
    height: 260,
  });

  const [section, setSection] = useState<Section>(openOn ?? 'players');
  const [who, setWho] = useState('');
  const [itemId, setItemId] = useState(items[0]?.id ?? 1);
  const [quantity, setQuantity] = useState(1);
  const [skill, setSkill] = useState(SKILL_NAMES[0] ?? 'Attack');
  const [level, setLevel] = useState(50);
  const [muteFor, setMuteFor] = useState(30);
  const [muteWhy, setMuteWhy] = useState('');
  const [look, setLook] = useState({
    skin: colourText(myLook.skin),
    eyes: colourText(myLook.eyes),
  });

  // Opened on a named section from the menu, which is how Players and Server
  // are one press rather than two. Compared against the last one asked for, so
  // pressing a tab in here is not undone the moment anything else rerenders.
  const [asked, setAsked] = useState(openOn);
  if (openOn !== undefined && openOn !== asked) {
    setAsked(openOn);
    setSection(openOn);
  }

  const sections: { key: Section; label: string; needs: RoleValue }[] = [
    { key: 'players', label: 'Players', needs: Role.Moderator },
    { key: 'world', label: 'World', needs: Role.SuperAdmin },
    { key: 'server', label: 'Server', needs: Role.SuperAdmin },
    { key: 'look', label: 'Appearance', needs: Role.Moderator },
    { key: 'peace', label: 'Moderation', needs: Role.Moderator },
  ];

  return (
    <div
      className={`trade admin-window${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}) }}
    >
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>Development tools</span>
        <CloseButton onClose={onClose} />
      </div>

      <div className="admin-tabs">
        {sections
          .filter((entry) => atLeast(role, entry.needs))
          .map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={section === entry.key ? 'tool on' : 'tool'}
              onClick={() => setSection(entry.key)}
            >
              {entry.label}
            </button>
          ))}
      </div>

      <div className="panel-body admin-body">
        {section === 'server' ? <ServerSection facts={facts} onCommand={onCommand} /> : null}
        {section === 'peace' ? (
          <ModerationSection role={role} facts={facts} onCommand={onCommand} />
        ) : null}

        {section === 'look' ? (
          <>
            <p className="hint">
              What your own character is drawn with.
              <Explain>
                Changed as it is picked, and everybody who can see you sees it change.
              </Explain>
            </p>
            <ColourChoice
              skin={look.skin}
              eyes={look.eyes}
              onChange={(which, colour) => {
                const next = { ...look, [which]: colour };
                setLook(next);
                onCommand('setLook', {
                  skin: colourValue(next.skin),
                  eyes: colourValue(next.eyes),
                });
              }}
            />
          </>
        ) : null}

        {section === 'world' ? (
          <>
            <p className="hint">
              The editor takes the world over.
              <Explain>
                Clicks pick, place and paint instead of moving your character. Play inside it gives
                the mouse back without closing it.
              </Explain>
            </p>
            <button
              type="button"
              className={editing ? 'wide on' : 'wide'}
              onClick={() => onEditing(!editing)}
            >
              {editing ? 'Close the world editor' : 'Open the world editor'}
            </button>

            {unsaved ? (
              <p className="hint warning">
                Unsaved work in the world.
                <Explain>
                  Save it before closing, or it is only in the server's memory until it restarts.
                </Explain>
              </p>
            ) : null}

            <TimeSection time={time} onCommand={onCommand} />
            <WorldMemorySection facts={facts} onCommand={onCommand} />
          </>
        ) : null}

        {section === 'players' ? (
          <>
            <p className="hint">
              {online.length === 1 ? 'One person online.' : `${online.length} online.`}
              <Explain>
                Press somebody to work on them. With nobody pressed, everything below is about your
                own character.
              </Explain>
            </p>

            <div className="admin-list">
              <button
                type="button"
                className={who === '' ? 'admin-row on' : 'admin-row'}
                onClick={() => setWho('')}
              >
                <span>Me</span>
              </button>

              {online.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={who === name ? 'admin-row on' : 'admin-row'}
                  onClick={() => setWho(name)}
                >
                  <span>{name}</span>
                </button>
              ))}
            </div>

            {/* Everything that can be done to or for whoever is pressed, in one
                place. Bringing somebody here and going to them used to be a tab
                away from giving them anything, which is two panels for one job. */}
            {who ? (
              <>
                <h3>{who}</h3>
                <div className="admin-actions wide-actions">
                  <button
                    type="button"
                    title="Bring them to where you are standing"
                    onClick={() => onCommand('fetch', {}, { player: who })}
                  >
                    Bring them here
                  </button>
                  <button
                    type="button"
                    title="Go to where they are standing"
                    onClick={() => onCommand('goTo', {}, { player: who })}
                  >
                    Go to them
                  </button>
                  {atLeast(role, Role.Moderator) ? (
                    <button
                      type="button"
                      className="danger"
                      title="Put them out of the world"
                      onClick={() => onCommand('kick', {}, { player: who })}
                    >
                      Kick
                    </button>
                  ) : null}
                </div>
                {atLeast(role, Role.Moderator) ? (
                  <div className="admin-actions wide-actions">
                    <select
                      value={muteFor}
                      onChange={(event) => setMuteFor(Number(event.target.value))}
                    >
                      <option value={5}>5 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>An hour</option>
                      <option value={1440}>A day</option>
                      <option value={10080}>A week</option>
                    </select>
                    <input
                      type="text"
                      maxLength={200}
                      placeholder="Why (they are told)"
                      value={muteWhy}
                      onChange={(event) => setMuteWhy(event.target.value)}
                    />
                    <button
                      type="button"
                      className="danger"
                      onClick={() =>
                        onCommand('mute', { minutes: muteFor }, { player: who, reason: muteWhy })
                      }
                    >
                      Mute
                    </button>
                    <button type="button" onClick={() => onCommand('unmute', {}, { player: who })}>
                      Unmute
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            {atLeast(role, Role.SuperAdmin) ? (
              <>
                <h3>Items</h3>
                <div className="setting">
                  <span>Item</span>
                  <select
                    value={itemId}
                    onChange={(event) => setItemId(Number(event.target.value))}
                  >
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="setting">
                  <span>How many</span>
                  <input
                    type="number"
                    min={1}
                    max={100000}
                    value={quantity}
                    onChange={(event) => setQuantity(Number(event.target.value))}
                  />
                </div>
                <button
                  type="button"
                  className="wide"
                  onClick={() =>
                    onCommand(
                      who ? 'giveTo' : 'give',
                      { item: itemId, quantity },
                      who ? { player: who } : {},
                    )
                  }
                >
                  {who ? `Give it to ${who}` : 'Give it to me'}
                </button>

                <h3>Skills</h3>
                <div className="setting">
                  <span>Skill</span>
                  <select value={skill} onChange={(event) => setSkill(event.target.value)}>
                    {SKILL_NAMES.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="setting">
                  <span>Level</span>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={level}
                    onChange={(event) => setLevel(Number(event.target.value))}
                  />
                </div>
                <button
                  type="button"
                  className="wide"
                  onClick={() =>
                    onCommand(
                      who ? 'setLevelOf' : 'setLevel',
                      { level },
                      who ? { player: who, skill } : { skill },
                    )
                  }
                >
                  Set it
                </button>
              </>
            ) : null}
          </>
        ) : null}
      </div>
      {grips}
    </div>
  );
}
